import React, { useState, useEffect } from 'react';
import apiFetch from '../../../utils/apiFetch';
import { SectionHead, Field, TextInput, SelectInput, CardSection, SaveBar } from '../components/ui';
import { COUNTRIES } from '../constants';
import { C } from '../styles';
import { authHeaders } from '../helpers';

interface Props {
  businessCountry: string;
  setBusinessCountry: (v: string) => void;
}

interface CompanyData {
  company_name: string;
  address_street: string;
  address_city: string;
  address_postal_code: string;
  business_number: string;
  payroll_rp_account: string;
  country: string;
  province_state: string;
  currency: string;
  fiscal_year_start: number;
  phone: string;
  industry: string;
  website: string;
}

const EMPTY: CompanyData = {
  company_name: '',
  address_street: '',
  address_city: '',
  address_postal_code: '',
  business_number: '',
  payroll_rp_account: '',
  country: 'us',
  province_state: '',
  currency: 'USD',
  fiscal_year_start: 1,
  phone: '',
  industry: '',
  website: '',
};

function hydrate(d: any): CompanyData {
  return {
    company_name: d.company_name || '',
    address_street: d.address_street || '',
    address_city: d.address_city || '',
    address_postal_code: d.address_postal_code || '',
    business_number: d.business_number || '',
    payroll_rp_account: d.payroll_rp_account || '',
    country: (d.country || 'US').toLowerCase(),
    province_state: d.province_state || '',
    currency: d.currency || 'USD',
    fiscal_year_start: d.fiscal_year_start || 1,
    phone: d.phone || '',
    industry: d.industry || '',
    website: d.website || '',
  };
}

export default function CompanyDetailsSection({ businessCountry, setBusinessCountry }: Props) {
  const [data, setData] = useState<CompanyData>(EMPTY);
  const [original, setOriginal] = useState<CompanyData>(EMPTY);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    apiFetch('/api/v1/company/profile', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          const initial = hydrate(d);
          setData(initial);
          setOriginal(initial);
          setBusinessCountry(initial.country);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const dirty = JSON.stringify(data) !== JSON.stringify(original);

  const onSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const payload = {
        company_name: data.company_name,
        address_street: data.address_street,
        address_city: data.address_city,
        address_postal_code: data.address_postal_code,
        business_number: data.business_number,
        payroll_rp_account: data.payroll_rp_account,
        country: (data.country || '').toUpperCase(),
        province_state: data.province_state,
        currency: data.currency,
        fiscal_year_start: parseInt(String(data.fiscal_year_start), 10) || 1,
        phone: data.phone,
        industry: data.industry,
        website: data.website,
      };
      const res = await apiFetch('/api/v1/company/profile', {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (res.ok) {
        const updated = await res.json();
        const refreshed = hydrate(updated);
        setData(refreshed);
        setOriginal(refreshed);
        setBusinessCountry(refreshed.country);
      } else {
        const err = await res.text();
        alert('Save failed: ' + err);
      }
    } catch (e: any) {
      alert('Save failed: ' + e.message);
    }
    setSaving(false);
  };

  const onDiscard = (): void => setData(original);
  const set = <K extends keyof CompanyData>(k: K, v: CompanyData[K]): void => {
    setData((s) => ({ ...s, [k]: v }));
  };

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading...</div>;

  return (
    <>
      <SectionHead title="Company details" subtitle="Your registered business information. Novala uses this on paycheques, tax filings, and CRA remittances." />
      <div>
        <CardSection label="Business">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Company name">
              <TextInput value={data.company_name} onChange={(v) => set('company_name', v)} placeholder="Your registered business name" />
            </Field>
            <Field label="Industry">
              <TextInput value={data.industry} onChange={(v) => set('industry', v)} placeholder="What industry you operate in" />
            </Field>
          </div>
        </CardSection>

        <CardSection label="Business address">
          <Field label="Street address">
            <TextInput value={data.address_street} onChange={(v) => set('address_street', v)} placeholder="e.g. 4805 152 Ave NW" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 14 }}>
            <Field label="City">
              <TextInput value={data.address_city} onChange={(v) => set('address_city', v)} placeholder="City" />
            </Field>
            <Field label="Province/State">
              <TextInput value={data.province_state} onChange={(v) => set('province_state', v)} placeholder="e.g. Alberta" />
            </Field>
            <Field label="Postal code">
              <TextInput value={data.address_postal_code} onChange={(v) => set('address_postal_code', v)} placeholder="T5X 0X0" />
            </Field>
          </div>
        </CardSection>

        <CardSection label="Location and currency">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Country">
              <SelectInput
                value={(data.country || '').toLowerCase()}
                onChange={(v) => {
                  set('country', v);
                  const c = COUNTRIES.find((x) => x.iso === v);
                  if (c) set('currency', c.currency);
                  setBusinessCountry(v);
                }}
              >
                {COUNTRIES.map((c) => <option key={c.iso} value={c.iso}>{c.name}</option>)}
              </SelectInput>
            </Field>
            <Field label="Base currency">
              <SelectInput value={data.currency} onChange={(v) => set('currency', v)}>
                <option value="USD">USD - US Dollar</option>
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="NZD">NZD - New Zealand Dollar</option>
                <option value="SGD">SGD - Singapore Dollar</option>
                <option value="JPY">JPY - Japanese Yen</option>
                <option value="ZAR">ZAR - South African Rand</option>
              </SelectInput>
            </Field>
          </div>
        </CardSection>

        <CardSection label="Fiscal year and time zone">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Fiscal year start month" help="Used for year-end payroll reporting. Most businesses use January.">
              <SelectInput value={String(data.fiscal_year_start || 1)} onChange={(v) => set('fiscal_year_start', parseInt(v, 10))}>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                  <option key={m} value={String(i + 1)}>{m}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Phone (optional)">
              <TextInput value={data.phone} onChange={(v) => set('phone', v)} placeholder="Business phone number" />
            </Field>
          </div>
        </CardSection>

        <SaveBar dirty={dirty} saving={saving} onSave={onSave} onDiscard={onDiscard} label="Save company details" />
      </div>
    </>
  );
}